/**
 * 我的聯絡人頁面（Phase 2）
 * - 左側群組面板：篩選 / 管理群組
 * - 右側聯絡人表格：checkbox 多選 + 批次刪除
 * - EE 商業模組完整 SOP ceremony
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, TextField, Typography, IconButton, Tooltip, Collapse,
  Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, Alert, Checkbox, Chip,
  List, ListItem, ListItemButton, ListItemText, ListItemIcon,
  Divider, Select, MenuItem, FormControl, InputLabel, Menu, TablePagination,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Group as GroupIcon,
  PersonOff as PersonOffIcon,
  PersonAdd as PersonAddIcon,
  DeleteSweep as DeleteSweepIcon,
  Edit as EditIcon,
  Sync as SyncIcon,
  LinkOff as LinkOffIcon,
  Tune as TuneIcon,
  ExpandMore as ExpandMoreIcon,
  Upload as UploadIcon,
  CameraAlt as CameraAltIcon,
  MoreVert as MoreVertIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { usePermission } from '../hooks/usePermission';
import { useMessage } from '../contexts/MessageContext';
import FunctionPageHeader from '../components/FunctionPageHeader';
import {
  getContacts, createContact, updateContact, deleteContact,
  getGoogleAuthUrl, getGoogleStatus, syncFromGoogle, disconnectGoogle,
  importVcard, scanCard, uploadCardImage, cardImageUrl,
  GoogleStatus,
} from '../services/eeMyContactsService';
import AuthImage from '../components/AuthImage';
import { getMyMailbox, getWebmailSsoUrl, UserMailbox } from '../services/mailService';
import {
  getGroups, createGroup, updateGroup, deleteGroup,
  addMembers, removeMembers, batchDeleteContacts,
} from '../services/eeMyContactGroupsService';
import { EeMyContact, EeMyContactCreate, EeMyContactUpdate } from '../types/eeMyContacts';
import { EeMyContactGroup, EeMyContactGroupCreate } from '../types/eeMyContactGroups';

const FUNC_CODE = 'ee_my_contacts';
const DRAWER_WIDTH = 220;

const emptyContactForm = (): EeMyContactCreate => ({
  display_name: '',
  first_name: '',
  last_name: '',
  org: '',
  title: '',
  phones: [],
  emails: [],
  is_active: true,
});

const EeMyContactsPage: React.FC = () => {
  const { t } = useTranslation();
  const { hasPermission, loading: permLoading } = usePermission();
  const { showSuccess, showApiError } = useMessage();
  const hasInitialized = useRef(false);

  // 資料狀態
  const [contacts, setContacts] = useState<EeMyContact[]>([]);
  const [groups, setGroups] = useState<EeMyContactGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  // null = 所有聯絡人 / number = 該群組 / 'ungrouped' = 未分組（#20）
  const [selectedGroupId, setSelectedGroupId] = useState<number | 'ungrouped' | null>(null);

  // #19 server-side 分頁：total=當前檢視(全部/群組)filter 後總數；allContactsTotal=所有聯絡人 distinct 總數
  const [total, setTotal] = useState(0);
  const [allContactsTotal, setAllContactsTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  // 郵箱狀態（決定 email 連結方式）
  const [userMailbox, setUserMailbox] = useState<UserMailbox | null>(null);
  // 左側群組面板收折
  const [showGroupPanel, setShowGroupPanel] = useState(true);
  // 設定面板
  const [showSettings, setShowSettings] = useState(false);
  // 群組重命名
  const [renamingGroupId, setRenamingGroupId] = useState<number | null>(null);
  const [renameInput, setRenameInput] = useState('');

  // 選取狀態
  const [selected, setSelected] = useState<Set<number>>(new Set());

  // 聯絡人 Dialog
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<EeMyContact | null>(null);
  const [contactForm, setContactForm] = useState<EeMyContactCreate>(emptyContactForm());
  const [saving, setSaving] = useState(false);

  // 刪除確認 Dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingContact, setDeletingContact] = useState<EeMyContact | null>(null);
  const [deleting, setDeleting] = useState(false);

  // 批次刪除確認
  const [batchDeleteDialogOpen, setBatchDeleteDialogOpen] = useState(false);
  const [batchDeleting, setBatchDeleting] = useState(false);

  // 群組 Dialog
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<EeMyContactGroup | null>(null);
  const [groupForm, setGroupForm] = useState<EeMyContactGroupCreate>({ name: '', color: '' });
  const [groupSaving, setGroupSaving] = useState(false);

  // 群組刪除確認
  const [groupDeleteDialogOpen, setGroupDeleteDialogOpen] = useState(false);
  const [deletingGroup, setDeletingGroup] = useState<EeMyContactGroup | null>(null);

  // Google OAuth 狀態
  const [googleStatus, setGoogleStatus] = useState<GoogleStatus | null>(null);
  const [googleSyncing, setGoogleSyncing] = useState(false);
  const [googleConnecting, setGoogleConnecting] = useState(false);

  // 匯入/新增指定群組
  const [syncGroupId, setSyncGroupId] = useState<number | null>(null);
  const [createContactGroupId, setCreateContactGroupId] = useState<number | null>(null);
  const [batchAddGroupId, setBatchAddGroupId] = useState<number | null>(null);

  // 群組顏色即時編輯（key=group.id, value=hex string）
  const [groupColorEdits, setGroupColorEdits] = useState<Record<number, string>>({});

  // vCard 匯入
  const vcardInputRef = useRef<HTMLInputElement>(null);
  const [vcardGroupId, setVcardGroupId] = useState<number | null>(null);
  const [vcardImporting, setVcardImporting] = useState(false);

  // 名片 OCR（新增聯絡人 dialog 內）
  const cardFrontInputRef = useRef<HTMLInputElement>(null);
  const cardBackInputRef = useRef<HTMLInputElement>(null);
  const [cardFrontPreview, setCardFrontPreview] = useState('');
  const [cardBackPreview, setCardBackPreview] = useState('');
  const [cardOcrLoading, setCardOcrLoading] = useState(false);
  const [cardFrontUrl, setCardFrontUrl] = useState('');
  const [cardBackUrl, setCardBackUrl] = useState('');
  // Task-32 G3.2：名片照 file_object id（平行 url，存進 card_image_file_object_ids）
  const [cardFrontFobjId, setCardFrontFobjId] = useState<number | null>(null);
  const [cardBackFobjId, setCardBackFobjId] = useState<number | null>(null);

  // 群組操作 Menu
  const [groupMenuAnchor, setGroupMenuAnchor] = useState<{ id: number; el: HTMLElement } | null>(null);

  // 編輯聯絡人時的群組異動（僅 edit 模式用）
  const [editGroupsRemove, setEditGroupsRemove] = useState<Set<string>>(new Set());
  const [editGroupsAdd, setEditGroupsAdd] = useState<number[]>([]);

  // #19：server-side 分頁 + 群組 filter。contacts = 當頁；total = filter 後總數。
  const loadContacts = useCallback(async () => {
    try {
      setLoading(true);
      const params: { search?: string; group_id?: number; no_group?: boolean; skip: number; limit: number } = {
        skip: page * pageSize,
        limit: pageSize,
      };
      if (search) params.search = search;
      if (selectedGroupId === 'ungrouped') params.no_group = true;
      else if (typeof selectedGroupId === 'number') params.group_id = selectedGroupId;
      const result = await getContacts(params);
      setContacts(result.items);
      setTotal(result.total);
      setSelected(new Set());
    } catch (err) { showApiError(err); }
    finally { setLoading(false); }
  }, [page, pageSize, search, selectedGroupId, showApiError]);

  // 「所有聯絡人」distinct 總數（獨立輕量查詢，limit=1 只取 total；不受群組選取影響）
  const refreshAllTotal = useCallback(async () => {
    try {
      const result = await getContacts({ skip: 0, limit: 1 });
      setAllContactsTotal(result.total);
    } catch { /* silent ok: 側欄總數失敗不阻斷主流程 */ }
  }, []);

  const loadGroups = useCallback(async () => {
    try {
      setGroups(await getGroups());
    } catch (err) { showApiError(err); }
  }, [showApiError]);

  // 異動後統一刷新（分頁下不做 optimistic 局部改，直接重抓當頁 + 群組計數 + 總數）
  const reloadData = useCallback(async () => {
    await Promise.all([loadContacts(), loadGroups(), refreshAllTotal()]);
  }, [loadContacts, loadGroups, refreshAllTotal]);

  const loadGoogleStatus = useCallback(async () => {
    try {
      setGoogleStatus(await getGoogleStatus());
    } catch { /* silent ok: Google API 未啟用時 400 為正常狀態(未連結帳號) */ }
  }, []);

  // mount 一次：群組 / Google / 郵箱 / 所有聯絡人總數
  useEffect(() => {
    if (!permLoading && hasPermission(FUNC_CODE, 'read') && !hasInitialized.current) {
      hasInitialized.current = true;
      loadGroups();
      loadGoogleStatus();
      refreshAllTotal();
      getMyMailbox().then(mb => setUserMailbox(mb)).catch(() => {});
    }
  }, [permLoading, hasPermission, loadGroups, loadGoogleStatus, refreshAllTotal]);

  // 當頁資料：page / pageSize / search / selectedGroupId 任一變即重抓（loadContacts 已含這些 dep）
  useEffect(() => {
    if (!permLoading && hasPermission(FUNC_CODE, 'read')) loadContacts();
  }, [permLoading, hasPermission, loadContacts]);

  // 換搜尋 / 換群組 → 回第一頁（避免停在超出範圍的頁）
  useEffect(() => { setPage(0); }, [search, selectedGroupId]);

  // 處理 OAuth 回呼的 URL 參數
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('google_connected') === '1') {
      loadGoogleStatus();
      loadContacts();
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (params.get('google_error')) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRenameGroup = async (id: number) => {
    if (!renameInput.trim()) return;
    try {
      const updated = await updateGroup(id, { name: renameInput.trim() });
      setGroups(prev => prev.map(g => g.id === id ? { ...g, name: updated.name } : g));
      showSuccess('SYS020001', { name: renameInput.trim() });
      setRenamingGroupId(null);
    } catch (err) { showApiError(err); }
  };

  const handleGroupColorChange = async (groupId: number, color: string) => {
    try {
      await updateGroup(groupId, { color });
      setGroups(prev => prev.map(g => g.id === groupId ? { ...g, color } : g));
    } catch (err) { showApiError(err); }
  };

  const handleVcardFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setVcardImporting(true);
    try {
      const stats = await importVcard(file, vcardGroupId);
      showSuccess('SYS310031', { name: `vCard 匯入：新增 ${stats.created} 筆，跳過 ${stats.skipped} 筆` });
      loadContacts();
      if (vcardGroupId) loadGroups();
    } catch (err) { showApiError(err); }
    finally { setVcardImporting(false); }
  };

  const handleCardImageUpload = async (file: File, side: 'front' | 'back') => {
    const previewUrl = URL.createObjectURL(file);
    if (side === 'front') setCardFrontPreview(previewUrl);
    else setCardBackPreview(previewUrl);
    setCardOcrLoading(true);
    try {
      const [{ parsed: p }, up] = await Promise.all([scanCard(file), uploadCardImage(file)]);
      if (side === 'front') { setCardFrontUrl(up.url); setCardFrontFobjId(up.file_object_id ?? null); }
      else { setCardBackUrl(up.url); setCardBackFobjId(up.file_object_id ?? null); }
      setContactForm(prev => {
        if (side === 'front') {
          return {
            ...prev,
            display_name: p.display_name || prev.display_name,
            org: p.org || prev.org,
            title: p.title || prev.title,
            phones: p.phones?.length ? p.phones : prev.phones,
            emails: p.emails?.length ? p.emails : prev.emails,
          };
        }
        // 背面：合併 phones / emails，去重
        const mergeUniq = <T extends { value: string }>(a: T[], b: T[]) =>
          [...a, ...b].filter((x, i, arr) => arr.findIndex(y => y.value === x.value) === i);
        return {
          ...prev,
          phones: mergeUniq(prev.phones ?? [], p.phones ?? []),
          emails: mergeUniq(prev.emails ?? [], p.emails ?? []),
        };
      });
    } catch (err) { showApiError(err); }
    finally { setCardOcrLoading(false); }
  };

  const handleOpenWebmailCompose = async (emailTo: string) => {
    const win = window.open('', '_blank');
    try {
      const url = await getWebmailSsoUrl(emailTo);
      if (win) win.location.href = url;
    } catch (err) {
      if (win) win.close();
      showApiError(err);
    }
  };

  const handleGoogleConnect = async () => {
    setGoogleConnecting(true);
    try {
      const returnTo = window.location.href.split('?')[0];
      const { auth_url } = await getGoogleAuthUrl(returnTo);
      window.location.href = auth_url;
    } catch (err) {
      showApiError(err);
      setGoogleConnecting(false);
    }
  };

  const handleGoogleSync = async () => {
    setGoogleSyncing(true);
    try {
      const result = await syncFromGoogle(syncGroupId);
      const { created, updated, skipped } = result.stats;
      showSuccess('SYS310038', { name: `新增 ${created}，更新 ${updated}，跳過 ${skipped}` });
      loadContacts();
      loadGroups();
    } catch (err) { showApiError(err); }
    finally { setGoogleSyncing(false); }
  };

  const handleGoogleDisconnect = async () => {
    try {
      await disconnectGoogle();
      setGoogleStatus({ connected: false });
    } catch (err) { showApiError(err); }
  };

  // #19：群組 filter 已移到 server-side（loadContacts 帶 group_id），contacts 即當頁結果。
  // 保留 displayContacts 別名以減少下游 JSX 改動（全選 / 表格 / 空狀態沿用）。
  const displayContacts = contacts;

  // #20：未分組人數 = 所有 distinct 總數 − 各群組成員數合計（client 算，免額外查詢）
  const ungroupedTotal = Math.max(
    0, allContactsTotal - groups.reduce((s, g) => s + (g.member_count ?? 0), 0),
  );

  // ── 選取 helpers ──
  const toggleSelect = (id: number) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    if (selected.size === displayContacts.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(displayContacts.map(c => c.id)));
    }
  };

  // ── 聯絡人 CRUD ──
  const openCreateContact = () => {
    setEditingContact(null);
    setContactForm(emptyContactForm());
    setCreateContactGroupId(null);
    setCardFrontPreview('');
    setCardBackPreview('');
    setCardFrontUrl('');
    setCardBackUrl('');
    setCardFrontFobjId(null);
    setCardBackFobjId(null);
    setContactDialogOpen(true);
  };
  const openEditContact = (c: EeMyContact) => {
    setEditingContact(c);
    setContactForm({
      display_name: c.display_name,
      first_name: c.first_name ?? '',
      last_name: c.last_name ?? '',
      org: c.org ?? '',
      title: c.title ?? '',
      phones: c.phones ?? [],
      emails: c.emails ?? [],
      is_active: c.is_active,
    });
    setEditGroupsRemove(new Set());
    setEditGroupsAdd([]);
    setContactDialogOpen(true);
  };
  const handleSaveContact = async () => {
    if (!contactForm.display_name.trim()) return;
    setSaving(true);
    try {
      if (editingContact) {
        await updateContact(editingContact.id, contactForm as EeMyContactUpdate);
        // 處理群組異動
        const contactId = editingContact.id;
        const groupOps: Promise<void>[] = [];
        for (const gname of Array.from(editGroupsRemove)) {
          const gid = groups.find(g => g.name === gname)?.id;
          if (gid) groupOps.push(removeMembers(gid, [contactId]));
        }
        for (const gid of editGroupsAdd) {
          groupOps.push(addMembers(gid, [contactId]));
        }
        if (groupOps.length) await Promise.all(groupOps);
        showSuccess('SYS310032', { name: contactForm.display_name });
      } else {
        // 平行收集 url + file_object_id（保 index 對齊；無 fobjid 存 0，reader 會 fallback）
        const cards = [
          { url: cardFrontUrl, fobj: cardFrontFobjId },
          { url: cardBackUrl, fobj: cardBackFobjId },
        ].filter(c => Boolean(c.url));
        const cardImages = cards.map(c => c.url);
        const cardFobjIds = cards.map(c => c.fobj ?? 0);
        const newContact = await createContact({
          ...contactForm,
          ...(cardImages.length ? { card_images: cardImages, card_image_file_object_ids: cardFobjIds } : {}),
        });
        if (createContactGroupId) {
          await addMembers(createContactGroupId, [newContact.id]);
        }
        showSuccess('SYS310031', { name: contactForm.display_name });
      }
      setContactDialogOpen(false);
      await reloadData();   // #19：分頁下重抓當頁 + 群組計數 + 總數（取代 optimistic）
    } catch (err) { showApiError(err); }
    finally { setSaving(false); }
  };
  const handleConfirmDelete = async () => {
    if (!deletingContact) return;
    setDeleting(true);
    try {
      await deleteContact(deletingContact.id);
      showSuccess('SYS310033', { name: deletingContact.display_name });
      setDeleteDialogOpen(false);
      setDeletingContact(null);
      await reloadData();   // #19：分頁下重抓當頁 + 群組計數 + 總數
    } catch (err) { showApiError(err); }
    finally { setDeleting(false); }
  };

  // ── 批次刪除 ──
  const handleBatchDelete = async () => {
    setBatchDeleting(true);
    const count = selected.size;
    const toDelete = Array.from(selected);
    try {
      if (typeof selectedGroupId === 'number') {
        await batchDeleteContacts(selectedGroupId, toDelete);
      } else {
        // 所有聯絡人 / 未分組（#20）→ 逐筆刪除
        await Promise.all(toDelete.map(id => deleteContact(id)));
      }
      setSelected(new Set());
      showSuccess('SYS310037', { count: String(count) });
      setBatchDeleteDialogOpen(false);
      await reloadData();   // #19：重抓當頁 + 群組計數 + 總數
    } catch (err) { showApiError(err); }
    finally { setBatchDeleting(false); }
  };

  // ── 群組 CRUD ──
  const handleSaveGroup = async () => {
    if (!groupForm.name.trim()) return;
    setGroupSaving(true);
    try {
      if (editingGroup) {
        const updated = await updateGroup(editingGroup.id, { name: groupForm.name, color: groupForm.color });
        setGroups(prev => prev.map(g => g.id === editingGroup.id ? { ...g, name: updated.name, color: updated.color } : g));
        showSuccess('SYS310035', { name: groupForm.name });
      } else {
        const newGroup = await createGroup(groupForm);
        setGroups(prev => [...prev, { ...newGroup, member_count: newGroup.member_count ?? 0 }]);
        showSuccess('SYS310034', { name: groupForm.name });
      }
      setGroupDialogOpen(false);
    } catch (err) { showApiError(err); }
    finally { setGroupSaving(false); }
  };
  const handleDeleteGroup = async () => {
    if (!deletingGroup) return;
    try {
      await deleteGroup(deletingGroup.id);
      setGroups(prev => prev.filter(g => g.id !== deletingGroup.id));
      showSuccess('SYS310036', { name: deletingGroup.name });
      setGroupDeleteDialogOpen(false);
      if (selectedGroupId === deletingGroup.id) setSelectedGroupId(null);
    } catch (err) { showApiError(err); }
  };

  // ── 加入群組（選取後直接加入當前群組）──
  const handleAddToGroup = async (groupId: number) => {
    if (selected.size === 0) return;
    const grpName = groups.find(g => g.id === groupId)?.name ?? '';
    try {
      await addMembers(groupId, Array.from(selected));
      showSuccess('SYS310034', { name: grpName });
      setSelected(new Set());
      await reloadData();   // #19：重抓當頁 group_names + 群組計數
    } catch (err) { showApiError(err); }
  };

  if (permLoading) return <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}><CircularProgress /></Box>;
  if (!hasPermission(FUNC_CODE, 'read')) return <Box p={3}><Alert severity="warning">{t('common.noPermission', '無此功能存取權限')}</Alert></Box>;

  return (
    <Box sx={{ display: 'flex', height: '100%' }}>
      {/* ── 左側群組面板 ── */}
      <Box sx={{
        width: showGroupPanel ? DRAWER_WIDTH : 32,
        flexShrink: 0,
        borderRight: 1,
        borderColor: 'divider',
        transition: 'width 0.2s',
        overflow: 'hidden',
        position: 'relative',
      }}>
        {/* 標題列 + 收折按鈕 */}
        <Box display="flex" justifyContent="space-between" alignItems="center" px={0.5} pt={1} pb={0.5}>
          {showGroupPanel && <Typography variant="subtitle2" color="text.secondary" sx={{ pl: 0.5 }}>群組</Typography>}
          <Box display="flex" alignItems="center" gap={0.25} ml={showGroupPanel ? 'auto' : 0}>
            {showGroupPanel && hasPermission(FUNC_CODE, 'create') && (
              <Tooltip title="新增群組">
                <IconButton size="small" onClick={() => { setEditingGroup(null); setGroupForm({ name: '', color: '' }); setGroupDialogOpen(true); }}>
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={showGroupPanel ? '收合群組' : '展開群組'}>
              <IconButton size="small" onClick={() => setShowGroupPanel(v => !v)}>
                <ExpandMoreIcon fontSize="small" sx={{ transform: showGroupPanel ? 'rotate(90deg)' : 'rotate(-90deg)', transition: '0.2s' }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* 面板內容（收折時隱藏）*/}
        {showGroupPanel && (
          <List dense disablePadding>
            <ListItem disablePadding>
              <ListItemButton selected={selectedGroupId === null} onClick={() => setSelectedGroupId(null)}>
                <ListItemIcon sx={{ minWidth: 32 }}><GroupIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="所有聯絡人" secondary={allContactsTotal} />
              </ListItemButton>
            </ListItem>
            {/* #20：未分組（不屬任何群組）*/}
            <ListItem disablePadding>
              <ListItemButton selected={selectedGroupId === 'ungrouped'} onClick={() => setSelectedGroupId('ungrouped')}>
                <ListItemIcon sx={{ minWidth: 32 }}><PersonOffIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary={t('myContacts.ungrouped', '未分組')} secondary={ungroupedTotal} />
              </ListItemButton>
            </ListItem>
            <Divider />
            {groups.map(g => (
              <ListItem key={g.id} disablePadding
                secondaryAction={
                  (hasPermission(FUNC_CODE, 'update') || hasPermission(FUNC_CODE, 'delete')) && renamingGroupId !== g.id && (
                    <IconButton size="small"
                      onClick={e => { e.stopPropagation(); setGroupMenuAnchor({ id: g.id, el: e.currentTarget }); }}>
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  )
                }
              >
                {renamingGroupId === g.id ? (
                  <Box display="flex" alignItems="center" gap={0.25} px={1} py={0.5} width="100%">
                    <TextField size="small" value={renameInput} autoFocus
                      onChange={e => setRenameInput(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') handleRenameGroup(g.id); if (e.key === 'Escape') setRenamingGroupId(null); }}
                      sx={{ flex: 1 }} inputProps={{ style: { padding: '2px 6px', fontSize: 13 } }} />
                    <IconButton size="small" onClick={() => handleRenameGroup(g.id)}><CheckIcon fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => setRenamingGroupId(null)}><CloseIcon fontSize="small" /></IconButton>
                  </Box>
                ) : (
                  <ListItemButton selected={selectedGroupId === g.id} onClick={() => setSelectedGroupId(g.id)}>
                    {g.color && <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: g.color, mr: 1, flexShrink: 0 }} />}
                    <ListItemText primary={g.name} secondary={g.member_count} />
                  </ListItemButton>
                )}
              </ListItem>
            ))}
          </List>
        )}
      </Box>

      {/* ── 右側主內容 ── */}
      <Box sx={{ flex: 1, pl: 2, overflow: 'auto' }}>
        <FunctionPageHeader funcCode={FUNC_CODE} />

        {/* 工具列 */}
        <Box display="flex" gap={1} mb={1} alignItems="center" flexWrap="wrap">
          {hasPermission(FUNC_CODE, 'create') && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateContact}>
              {t('contacts.add', '新增聯絡人')}
            </Button>
          )}
          {/* hidden input — vCard 匯入 */}
          <input ref={vcardInputRef} type="file" accept=".vcf,text/vcard" hidden onChange={handleVcardFileChange} />
          <Tooltip title={showSettings ? '收合設定' : '展開設定'}>
            <Button
              size="small" variant="outlined"
              startIcon={<TuneIcon fontSize="small" />}
              endIcon={<ExpandMoreIcon fontSize="small" sx={{ transform: showSettings ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />}
              onClick={() => setShowSettings(v => !v)}
            >
              設定
            </Button>
          </Tooltip>
          <TextField
            size="small"
            placeholder={t('contacts.search', '搜尋姓名/公司/Email')}
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && loadContacts()}
            InputProps={{ endAdornment: <IconButton size="small" onClick={loadContacts}><SearchIcon fontSize="small" /></IconButton> }}
            sx={{ width: 220 }}
          />
          {loading && <CircularProgress size={20} />}

          {/* 批次操作（有選取時出現）*/}
          {selected.size > 0 && (
            <>
              <Chip label={`已選 ${selected.size} 筆`} size="small" color="primary" />
              {hasPermission(FUNC_CODE, 'delete') && (
                <Button variant="outlined" color="error" size="small"
                  startIcon={<DeleteSweepIcon />}
                  onClick={() => setBatchDeleteDialogOpen(true)}>
                  批次刪除
                </Button>
              )}
              {groups.length > 0 && hasPermission(FUNC_CODE, 'update') && (
                <Box display="flex" gap={0.5} alignItems="center">
                  <FormControl size="small" sx={{ minWidth: 110 }}>
                    <InputLabel>加入群組</InputLabel>
                    <Select
                      value={batchAddGroupId ?? ''}
                      label="加入群組"
                      onChange={e => setBatchAddGroupId(e.target.value ? Number(e.target.value) : null)}
                    >
                      <MenuItem value="">選擇群組</MenuItem>
                      {groups.map(g => <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <Button variant="outlined" size="small"
                    startIcon={<PersonAddIcon />}
                    disabled={!batchAddGroupId}
                    onClick={() => { if (batchAddGroupId) handleAddToGroup(batchAddGroupId); }}>
                    加入
                  </Button>
                </Box>
              )}
            </>
          )}
        </Box>

        {/* 設定面板 */}
        <Collapse in={showSettings}>
          <Box display="flex" gap={2} mb={2} alignItems="flex-start" flexWrap="wrap"
               sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}>

            {/* Google 同步 */}
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Google 聯絡人同步</Typography>
              <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                {googleStatus?.connected ? (
                  <>
                    <Chip label={googleStatus.expired ? 'Google（已過期）' : 'Google 已連結'}
                      color={googleStatus.expired ? 'warning' : 'success'} size="small" variant="outlined" />
                    {groups.length > 0 && (
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>匯入到群組</InputLabel>
                        <Select
                          value={syncGroupId ?? ''}
                          label="匯入到群組"
                          onChange={e => setSyncGroupId(e.target.value ? Number(e.target.value) : null)}
                        >
                          <MenuItem value="">不指定</MenuItem>
                          {groups.map(g => <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>)}
                        </Select>
                      </FormControl>
                    )}
                    <Button variant="outlined" size="small"
                      startIcon={googleSyncing ? <CircularProgress size={14} /> : <SyncIcon />}
                      onClick={handleGoogleSync} disabled={googleSyncing || !!googleStatus.expired}>
                      從 Google 匯入
                    </Button>
                    <Tooltip title="取消連結 Google">
                      <IconButton size="small" color="error" onClick={handleGoogleDisconnect}>
                        <LinkOffIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                ) : (
                  <Button variant="outlined" size="small"
                    startIcon={googleConnecting ? <CircularProgress size={14} /> : <SyncIcon />}
                    onClick={handleGoogleConnect} disabled={googleConnecting}
                    sx={{ borderColor: '#4285f4', color: '#4285f4', '&:hover': { borderColor: '#1a73e8', bgcolor: '#e8f0fe' } }}>
                    連結 Google 聯絡人
                  </Button>
                )}
              </Box>
            </Box>

            {hasPermission(FUNC_CODE, 'create') && (
              <>
                <Divider orientation="vertical" flexItem />
                {/* 匯入聯絡人 */}
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>匯入聯絡人</Typography>
                  <Box display="flex" flexDirection="row" gap={1} alignItems="center" flexWrap="wrap">
                    {groups.length > 0 && (
                      <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>匯入到群組</InputLabel>
                        <Select
                          value={vcardGroupId ?? ''}
                          label="匯入到群組"
                          onChange={e => setVcardGroupId(e.target.value ? Number(e.target.value) : null)}
                        >
                          <MenuItem value="">不指定</MenuItem>
                          {groups.map(g => <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>)}
                        </Select>
                      </FormControl>
                    )}
                    <Tooltip title="匯入 .vcf 名片檔">
                      <Button variant="outlined" size="small"
                        startIcon={vcardImporting ? <CircularProgress size={14} /> : <UploadIcon />}
                        onClick={() => vcardInputRef.current?.click()}
                        disabled={vcardImporting}>
                        匯入 vCard
                      </Button>
                    </Tooltip>
                  </Box>
                </Box>
              </>
            )}

          </Box>
        </Collapse>

        {/* 聯絡人表格 */}
        <Box sx={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
                <th style={{ width: 40, padding: '8px 4px' }}>
                  <Checkbox size="small"
                    checked={selected.size === displayContacts.length && displayContacts.length > 0}
                    indeterminate={selected.size > 0 && selected.size < displayContacts.length}
                    onChange={toggleSelectAll} />
                </th>
                <th style={{ textAlign: 'left', padding: '8px' }}>姓名</th>
                <th style={{ textAlign: 'left', padding: '8px' }}>公司/職稱</th>
                <th style={{ textAlign: 'left', padding: '8px' }}>Email</th>
                <th style={{ textAlign: 'left', padding: '8px' }}>電話</th>
                <th style={{ textAlign: 'left', padding: '8px' }}>群組</th>
                <th style={{ width: 80, padding: '8px' }}></th>
              </tr>
            </thead>
            <tbody>
              {displayContacts.map(c => (
                <tr key={c.id} style={{ borderBottom: '1px solid #f0f0f0' }}
                  onDoubleClick={() => hasPermission(FUNC_CODE, 'update') && openEditContact(c)}>
                  <td style={{ padding: '6px 4px' }}>
                    <Checkbox size="small" checked={selected.has(c.id)} onChange={() => toggleSelect(c.id)} />
                  </td>
                  <td style={{ padding: '6px 8px' }}>
                    <Typography variant="body2" fontWeight={500}>{c.display_name}</Typography>
                    {(c.first_name || c.last_name) && (
                      <Typography variant="caption" color="text.secondary">
                        {[c.last_name, c.first_name].filter(Boolean).join(' ')}
                      </Typography>
                    )}
                  </td>
                  <td style={{ padding: '6px 8px' }}>
                    {c.org && <Typography variant="body2">{c.org}</Typography>}
                    {c.title && <Typography variant="caption" color="text.secondary">{c.title}</Typography>}
                  </td>
                  <td style={{ padding: '6px 8px' }}>
                    {c.emails?.[0]?.value ? (
                      userMailbox?.server_exists
                        ? <Tooltip title="以 Webmail 寄信">
                            <a href="#" onClick={e => { e.preventDefault(); handleOpenWebmailCompose(c.emails![0].value); }}
                               style={{ color: 'inherit', textDecoration: 'none' }}
                               onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
                               onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}>
                              <Typography variant="body2">{c.emails[0].value}</Typography>
                            </a>
                          </Tooltip>
                        : <a href={`mailto:${c.emails[0].value}`} style={{ color: 'inherit', textDecoration: 'none' }}
                             onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
                             onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}>
                            <Typography variant="body2">{c.emails[0].value}</Typography>
                          </a>
                    ) : <Typography variant="body2">—</Typography>}
                  </td>
                  <td style={{ padding: '6px 8px' }}>
                    <Typography variant="body2">
                      {c.phones?.[0]?.value ?? '—'}
                    </Typography>
                  </td>
                  <td style={{ padding: '6px 8px' }}>
                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                      {(c.group_names ?? []).map(gname => {
                        const grp = groups.find(g => g.name === gname);
                        return (
                          <Chip key={gname} label={gname} size="small" variant="outlined"
                            sx={{ height: 18, fontSize: 11, bgcolor: grp?.color ? `${grp.color}22` : undefined,
                                  borderColor: grp?.color ?? undefined }} />
                        );
                      })}
                    </Box>
                  </td>
                  <td style={{ padding: '6px 4px', textAlign: 'center' }}>
                    {hasPermission(FUNC_CODE, 'update') && (
                      <Tooltip title="編輯">
                        <IconButton size="small" onClick={() => openEditContact(c)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {hasPermission(FUNC_CODE, 'delete') && (
                      <Tooltip title="刪除">
                        <IconButton size="small" color="error"
                          onClick={() => { setDeletingContact(c); setDeleteDialogOpen(true); }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </td>
                </tr>
              ))}
              {displayContacts.length === 0 && !loading && (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: 32, color: '#999' }}>尚無聯絡人</td></tr>
              )}
            </tbody>
          </table>
        </Box>

        {/* #19 server-side 分頁：count=當前檢視 filter 後總數 */}
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[25, 50, 100, 200]}
          labelRowsPerPage={t('common.rowsPerPage', '每頁筆數:')}
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} / ${count}`}
        />
      </Box>

      {/* hidden inputs — 名片 OCR */}
      <input ref={cardFrontInputRef} type="file" accept="image/*" hidden
        onChange={e => { const f = e.target.files?.[0]; if (f) handleCardImageUpload(f, 'front'); e.target.value = ''; }} />
      <input ref={cardBackInputRef} type="file" accept="image/*" hidden
        onChange={e => { const f = e.target.files?.[0]; if (f) handleCardImageUpload(f, 'back'); e.target.value = ''; }} />

      {/* ── 新增/編輯聯絡人 Dialog ── */}
      <Dialog open={contactDialogOpen} onClose={() => setContactDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingContact ? t('contacts.editTitle', '編輯聯絡人') : t('contacts.addTitle', '新增聯絡人')}</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>

            {/* 名片圖片（編輯時顯示已儲存圖片）*/}
            {editingContact && (editingContact.card_images ?? []).length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>名片圖片</Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  {(editingContact.card_images ?? []).map((_url, idx) => (
                    // G3.2：走加密 file_object reader（AuthImage 帶 token）；reader 內 fallback 舊明文
                    <AuthImage key={idx} src={cardImageUrl(editingContact.id, idx)} alt=""
                      style={{ width: 110, height: 68, objectFit: 'cover', borderRadius: 4, border: '1px solid #e0e0e0' }} />
                  ))}
                </Box>
              </Box>
            )}

            {/* 名片 OCR 區塊（新增時才顯示）*/}
            {!editingContact && (
              <Box sx={{ p: 1.5, border: '1px dashed', borderColor: 'divider', borderRadius: 1, bgcolor: 'grey.50' }}>
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                  從名片 OCR 自動填入（可選）
                </Typography>
                <Box display="flex" gap={1.5} alignItems="center">
                  {/* 正面 */}
                  <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
                    <Box
                      onClick={() => cardFrontInputRef.current?.click()}
                      sx={{ width: 110, height: 68, borderRadius: 1, border: '1px solid', borderColor: 'divider',
                            overflow: 'hidden', cursor: 'pointer', display: 'flex', alignItems: 'center',
                            justifyContent: 'center', bgcolor: 'white', '&:hover': { borderColor: 'primary.main' } }}>
                      {cardFrontPreview
                        ? <Box component="img" src={cardFrontPreview} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : <Box textAlign="center"><CameraAltIcon sx={{ fontSize: 24, color: 'text.disabled' }} /><Typography variant="caption" display="block" color="text.disabled">正面</Typography></Box>
                      }
                    </Box>
                    <Typography variant="caption" color="text.secondary">正面</Typography>
                  </Box>
                  {/* 背面 */}
                  <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
                    <Box
                      onClick={() => cardBackInputRef.current?.click()}
                      sx={{ width: 110, height: 68, borderRadius: 1, border: '1px solid', borderColor: 'divider',
                            overflow: 'hidden', cursor: 'pointer', display: 'flex', alignItems: 'center',
                            justifyContent: 'center', bgcolor: 'white', '&:hover': { borderColor: 'primary.main' } }}>
                      {cardBackPreview
                        ? <Box component="img" src={cardBackPreview} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : <Box textAlign="center"><CameraAltIcon sx={{ fontSize: 24, color: 'text.disabled' }} /><Typography variant="caption" display="block" color="text.disabled">背面</Typography></Box>
                      }
                    </Box>
                    <Typography variant="caption" color="text.secondary">背面</Typography>
                  </Box>
                  {cardOcrLoading && (
                    <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
                      <CircularProgress size={20} />
                      <Typography variant="caption" color="text.secondary">OCR 中…</Typography>
                    </Box>
                  )}
                  {(cardFrontPreview || cardBackPreview) && !cardOcrLoading && (
                    <Typography variant="caption" color="success.main" sx={{ flex: 1 }}>
                      已自動填入，請確認欄位
                    </Typography>
                  )}
                </Box>
              </Box>
            )}

            <TextField label={t('contacts.displayName', '顯示名稱')} required size="small" fullWidth
              value={contactForm.display_name}
              onChange={e => setContactForm({ ...contactForm, display_name: e.target.value })} />
            <Box display="flex" gap={1}>
              <TextField label={t('contacts.lastName', '姓')} size="small" fullWidth
                value={contactForm.last_name ?? ''}
                onChange={e => setContactForm({ ...contactForm, last_name: e.target.value })} />
              <TextField label={t('contacts.firstName', '名')} size="small" fullWidth
                value={contactForm.first_name ?? ''}
                onChange={e => setContactForm({ ...contactForm, first_name: e.target.value })} />
            </Box>
            <TextField label={t('contacts.org', '公司/單位')} size="small" fullWidth
              value={contactForm.org ?? ''}
              onChange={e => setContactForm({ ...contactForm, org: e.target.value })} />
            <TextField label={t('contacts.title', '職稱')} size="small" fullWidth
              value={contactForm.title ?? ''}
              onChange={e => setContactForm({ ...contactForm, title: e.target.value })} />
            <TextField label={t('contacts.email', 'Email')} size="small" fullWidth
              value={contactForm.emails?.[0]?.value ?? ''}
              onChange={e => setContactForm({
                ...contactForm,
                emails: e.target.value ? [{ type: 'work', value: e.target.value }] : [],
              })} />
            <TextField label={t('contacts.phone', '電話')} size="small" fullWidth
              value={contactForm.phones?.[0]?.value ?? ''}
              onChange={e => setContactForm({
                ...contactForm,
                phones: e.target.value ? [{ type: 'mobile', value: e.target.value }] : [],
              })} />
            {/* 編輯模式：群組管理 */}
            {editingContact && groups.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.75}>群組</Typography>
                {/* 現有群組 Chips — 點 X 標記移除，再點取消 */}
                <Box display="flex" flexWrap="wrap" gap={0.5} mb={1}>
                  {(editingContact.group_names ?? []).map(gname => {
                    const removing = editGroupsRemove.has(gname);
                    const grp = groups.find(g => g.name === gname);
                    return (
                      <Chip
                        key={gname}
                        label={gname}
                        size="small"
                        variant={removing ? 'outlined' : 'filled'}
                        sx={{
                          bgcolor: removing ? undefined : (grp?.color ? `${grp.color}33` : 'primary.main'),
                          color: removing ? 'text.disabled' : (grp?.color ? grp.color : 'primary.contrastText'),
                          borderColor: removing ? 'divider' : (grp?.color ?? undefined),
                          textDecoration: removing ? 'line-through' : 'none',
                          opacity: removing ? 0.55 : 1,
                        }}
                        onDelete={() => setEditGroupsRemove(prev => {
                          const next = new Set(prev);
                          next.has(gname) ? next.delete(gname) : next.add(gname);
                          return next;
                        })}
                        deleteIcon={removing
                          ? <Tooltip title="取消移除"><CloseIcon /></Tooltip>
                          : <Tooltip title="從群組移除"><CloseIcon /></Tooltip>}
                      />
                    );
                  })}
                  {/* 待新增群組（綠色預覽） */}
                  {editGroupsAdd.map(gid => {
                    const g = groups.find(g => g.id === gid);
                    if (!g) return null;
                    return (
                      <Chip key={gid} label={`＋${g.name}`} size="small" color="success"
                        onDelete={() => setEditGroupsAdd(prev => prev.filter(id => id !== gid))} />
                    );
                  })}
                </Box>
                {/* 加入新群組 */}
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>加入群組</InputLabel>
                  <Select
                    value=""
                    label="加入群組"
                    onChange={e => {
                      const gid = Number(e.target.value);
                      if (!gid) return;
                      if (editGroupsAdd.includes(gid)) return;
                      const alreadyIn = (editingContact.group_names ?? []).includes(groups.find(g => g.id === gid)?.name ?? '');
                      if (alreadyIn && !editGroupsRemove.has(groups.find(g => g.id === gid)?.name ?? '')) return;
                      setEditGroupsAdd(prev => [...prev, gid]);
                    }}
                  >
                    <MenuItem value="">— 選擇群組 —</MenuItem>
                    {groups
                      .filter(g => {
                        const alreadyIn = (editingContact.group_names ?? []).includes(g.name) && !editGroupsRemove.has(g.name);
                        return !alreadyIn && !editGroupsAdd.includes(g.id);
                      })
                      .map(g => <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Box>
            )}

            {/* 新增模式：加入群組 */}
            {!editingContact && groups.length > 0 && (
              <FormControl size="small" fullWidth>
                <InputLabel>加入群組（可選）</InputLabel>
                <Select
                  value={createContactGroupId ?? ''}
                  label="加入群組（可選）"
                  onChange={e => setCreateContactGroupId(e.target.value ? Number(e.target.value) : null)}
                >
                  <MenuItem value="">不加入群組</MenuItem>
                  {groups.map(g => <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>)}
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContactDialogOpen(false)} disabled={saving}>{t('common.cancel', '取消')}</Button>
          <Button variant="contained" onClick={handleSaveContact}
            disabled={saving || !contactForm.display_name.trim()}>
            {saving ? <CircularProgress size={16} /> : t('common.save', '儲存')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── 刪除確認 ── */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs">
        <DialogTitle>{t('common.confirmDelete', '確認刪除')}</DialogTitle>
        <DialogContent>
          <Typography>{t('contacts.deleteConfirm', '確定要刪除聯絡人「{{name}}」嗎？', { name: deletingContact?.display_name })}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>{t('common.cancel', '取消')}</Button>
          <Button variant="contained" color="error" onClick={handleConfirmDelete} disabled={deleting}>
            {deleting ? <CircularProgress size={16} /> : t('common.delete', '刪除')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── 批次刪除確認 ── */}
      <Dialog open={batchDeleteDialogOpen} onClose={() => setBatchDeleteDialogOpen(false)} maxWidth="xs">
        <DialogTitle>確認批次刪除</DialogTitle>
        <DialogContent>
          <Typography>確定要刪除選取的 {selected.size} 筆聯絡人嗎？此操作無法復原。</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBatchDeleteDialogOpen(false)} disabled={batchDeleting}>{t('common.cancel', '取消')}</Button>
          <Button variant="contained" color="error" onClick={handleBatchDelete} disabled={batchDeleting}>
            {batchDeleting ? <CircularProgress size={16} /> : `刪除 ${selected.size} 筆`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── 新增/編輯群組 Dialog ── */}
      <Dialog open={groupDialogOpen} onClose={() => setGroupDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editingGroup ? '編輯群組' : '新增群組'}</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField label="群組名稱" required size="small" fullWidth
              value={groupForm.name}
              onChange={e => setGroupForm({ ...groupForm, name: e.target.value })} />
            <Box display="flex" alignItems="center" gap={1.5}>
              <Typography variant="body2" color="text.secondary">群組顏色</Typography>
              <input
                type="color"
                value={groupForm.color || '#1976d2'}
                onChange={e => setGroupForm({ ...groupForm, color: e.target.value })}
                style={{ width: 40, height: 32, padding: 2, cursor: 'pointer', border: '1px solid #ccc', borderRadius: 4 }}
              />
              <Typography variant="caption" color="text.secondary">{groupForm.color || '#1976d2'}</Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGroupDialogOpen(false)} disabled={groupSaving}>{t('common.cancel', '取消')}</Button>
          <Button variant="contained" onClick={handleSaveGroup}
            disabled={groupSaving || !groupForm.name.trim()}>
            {groupSaving ? <CircularProgress size={16} /> : t('common.save', '儲存')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── 群組操作 Menu ── */}
      <Menu
        anchorEl={groupMenuAnchor?.el}
        open={Boolean(groupMenuAnchor)}
        onClose={() => setGroupMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {hasPermission(FUNC_CODE, 'update') && (
          <MenuItem onClick={e => e.stopPropagation()} disableRipple dense>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" sx={{ minWidth: 36 }}>顏色</Typography>
              <input
                type="color"
                value={groupColorEdits[groupMenuAnchor?.id ?? 0] ?? groups.find(g => g.id === groupMenuAnchor?.id)?.color ?? '#1976d2'}
                onChange={e => { if (groupMenuAnchor) setGroupColorEdits(prev => ({ ...prev, [groupMenuAnchor.id]: e.target.value })); }}
                onBlur={e => { if (groupMenuAnchor) handleGroupColorChange(groupMenuAnchor.id, e.target.value); }}
                style={{ width: 30, height: 24, padding: 1, cursor: 'pointer', border: '1px solid #ccc', borderRadius: 4 }}
              />
            </Box>
          </MenuItem>
        )}
        {hasPermission(FUNC_CODE, 'update') && (
          <MenuItem dense onClick={() => {
            const g = groups.find(g => g.id === groupMenuAnchor?.id);
            if (g) { setRenamingGroupId(g.id); setRenameInput(g.name); }
            setGroupMenuAnchor(null);
          }}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />變更名稱
          </MenuItem>
        )}
        {hasPermission(FUNC_CODE, 'delete') && (
          <MenuItem dense sx={{ color: 'error.main' }} onClick={() => {
            const g = groups.find(g => g.id === groupMenuAnchor?.id);
            if (g) { setDeletingGroup(g); setGroupDeleteDialogOpen(true); }
            setGroupMenuAnchor(null);
          }}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />刪除群組
          </MenuItem>
        )}
      </Menu>

      {/* ── 群組刪除確認 ── */}
      <Dialog open={groupDeleteDialogOpen} onClose={() => setGroupDeleteDialogOpen(false)} maxWidth="xs">
        <DialogTitle>確認刪除群組</DialogTitle>
        <DialogContent>
          <Typography>確定要刪除群組「{deletingGroup?.name}」嗎？聯絡人本身不會被刪除。</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGroupDeleteDialogOpen(false)}>{t('common.cancel', '取消')}</Button>
          <Button variant="contained" color="error" onClick={handleDeleteGroup}>{t('common.delete', '刪除')}</Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default EeMyContactsPage;
